import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { cn } from '@/lib/utils';

/**
 * Layout 3-col redimensionável do Workspace (E13.F7a).
 *
 * Acima do breakpoint xl (1280px): PanelGroup horizontal com 2 resize handles.
 * Abaixo: colunas empilhadas (single-column flex-col), sem resize, para mobile/tablet.
 *
 * Persistência: as porcentagens são gravadas em `localStorage` sob a chave
 * `signcheck.workspace.layout`. Fallback para defaults se chave ausente ou
 * dado corrompido.
 */
export const WORKSPACE_LAYOUT_STORAGE_KEY = 'signcheck.workspace.layout';
/**
 * Proporção da prancha 2B (E54.F5.T04): 262px / 1fr / 300px sobre a largura de
 * conteúdo de 1204px (1440 menos a barra lateral de 236px). Em porcentagem
 * porque o painel é redimensionável — o que a prancha fixa é a proporção de
 * partida, não uma trava. Layout já salvo pelo perito continua vencendo.
 */
const DEFAULT_SIZES = [22, 53, 25] as const;
const MIN_LEFT = 12;
const MIN_CENTER = 30;
const MIN_RIGHT = 16;

export interface WorkspaceResizableLayoutProps {
  left: ReactNode;
  center: ReactNode;
  right: ReactNode;
  /** Override do storage para testes. */
  storage?: Storage;
  /** Força modo single-column (útil para tests). Default: autodetect via matchMedia. */
  forceSingleColumn?: boolean;
  /** E37.F3.T03: modo foco — recolhe as colunas laterais (esquerda/direita) para
   * análise imersiva. Implementado por classe CSS (não troca a árvore), então a
   * coluna central NÃO remonta e preserva o estado da mesa (zoom/transform/modo);
   * sair do foco restaura os tamanhos anteriores (o react-resizable-panels mantém
   * o estado interno, só sobrepomos a exibição). */
  focusMode?: boolean;
}

function readPersistedSizes(storage: Storage): readonly number[] | null {
  try {
    const raw = storage.getItem(WORKSPACE_LAYOUT_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== 3) return null;
    if (!parsed.every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0)) return null;
    const sum = parsed.reduce((a, b) => a + b, 0);
    if (Math.abs(sum - 100) > 0.5) return null;
    return parsed as number[];
  } catch {
    return null;
  }
}

function useXlMatch(forceSingleColumn?: boolean): boolean {
  const [isXl, setIsXl] = useState<boolean>(() => {
    if (forceSingleColumn) return false;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
    return window.matchMedia('(min-width: 1280px)').matches;
  });
  useEffect(() => {
    if (forceSingleColumn) {
      setIsXl(false);
      return;
    }
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(min-width: 1280px)');
    const handler = (e: MediaQueryListEvent) => setIsXl(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [forceSingleColumn]);
  return isXl;
}

export function WorkspaceResizableLayout({
  left,
  center,
  right,
  storage,
  forceSingleColumn,
  focusMode = false,
}: WorkspaceResizableLayoutProps) {
  const effectiveStorage = useMemo<Storage | null>(() => {
    if (storage) return storage;
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  }, [storage]);

  const initialSizes = useMemo<readonly number[]>(() => {
    if (effectiveStorage === null) return DEFAULT_SIZES;
    return readPersistedSizes(effectiveStorage) ?? DEFAULT_SIZES;
  }, [effectiveStorage]);

  const isXl = useXlMatch(forceSingleColumn);

  function handleLayoutChange(sizes: number[]): void {
    if (effectiveStorage === null) return;
    if (sizes.length !== 3) return;
    try {
      effectiveStorage.setItem(WORKSPACE_LAYOUT_STORAGE_KEY, JSON.stringify(sizes));
    } catch {
      // QuotaExceeded ou storage indisponível: ignora silenciosamente.
    }
  }

  if (!isXl) {
    return (
      <div
        data-testid="workspace-resizable-layout"
        data-mode="stacked"
        data-focus={focusMode ? 'on' : 'off'}
        className={cn(
          'flex min-h-[calc(100vh-12rem)] flex-col gap-4',
          focusMode && 'workspace-layout--focus',
        )}
      >
        <section className="workspace-left">{left}</section>
        <section className="workspace-middle">{center}</section>
        <section className="workspace-right">{right}</section>
      </div>
    );
  }

  return (
    <PanelGroup
      direction="horizontal"
      autoSaveId={undefined}
      onLayout={handleLayoutChange}
      data-testid="workspace-resizable-layout"
      data-mode="resizable"
      data-focus={focusMode ? 'on' : 'off'}
      className={cn('min-h-[calc(100vh-12rem)] gap-0', focusMode && 'workspace-layout--focus')}
    >
      <Panel
        defaultSize={initialSizes[0]}
        minSize={MIN_LEFT}
        className="workspace-left"
        data-testid="workspace-panel-left"
      >
        {left}
      </Panel>
      <PanelResizeHandle
        className={cn(
          'workspace-resize-handle w-1 cursor-col-resize bg-transparent transition-colors',
          'hover:bg-accent/40 data-[resize-handle-state=drag]:bg-accent',
        )}
        data-testid="workspace-resize-handle-left"
      />
      <Panel
        defaultSize={initialSizes[1]}
        minSize={MIN_CENTER}
        className="workspace-middle"
        data-testid="workspace-panel-center"
      >
        {center}
      </Panel>
      <PanelResizeHandle
        className={cn(
          'workspace-resize-handle w-1 cursor-col-resize bg-transparent transition-colors',
          'hover:bg-accent/40 data-[resize-handle-state=drag]:bg-accent',
        )}
        data-testid="workspace-resize-handle-right"
      />
      <Panel
        defaultSize={initialSizes[2]}
        minSize={MIN_RIGHT}
        className="workspace-right"
        data-testid="workspace-panel-right"
      >
        {right}
      </Panel>
    </PanelGroup>
  );
}
